// src/components/tokenPage/performance/TokenVsBenchmarksReturnsChart.tsx

import React, { useMemo, useState } from "react";
import { ChartCard } from "@/components/charts";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { getSeriesColorForName } from "@/utils/colors";
import { TimeSeriesChart } from "@/components/charts/base";
import { formatDateLabel } from "@/utils/charts/dataUtils";
import { useToken } from "@/contexts";
import { useTokenPrice } from "@/contexts/token";
import { sliceByRange, pricesToCumulativePercent } from "@/lib/metrics";

type RangeKey = '30d' | '90d' | '180d' | '365d' | 'All';

/**
 * Returns vs BTC/ETH Chart
 * 
 * Shows cumulative returns comparison between token and benchmarks.
 * All calculations use lib/metrics functions.
 */
const TokenVsBenchmarksReturnsChart: React.FC = () => {
  const { ticker } = useToken();
  const { token, btcPrices, ethPrices, isLoading } = useTokenPrice();
  const [range, setRange] = useState<RangeKey>('90d');

  const tkr = ticker.toUpperCase();
  const prices = token.prices;

  const { dates, series } = useMemo(() => {
    const empty = { dates: [] as string[], series: [] as Array<{ name: string; values: number[]; color: string; areaStyle: boolean }> };
    
    if (prices.values.length < 2) {
      return empty;
    }

    // Slice by range using price dates (not aligned returns dates)
    const targetLen = range === '30d' ? 30 : range === '90d' ? 90 : range === '180d' ? 180 : range === '365d' ? 365 : prices.dates.length;
    const { dates: dateSlice, values: priceSlice } = sliceByRange(prices.dates, prices.values, targetLen);
    const { values: btcSlice } = sliceByRange(prices.dates, btcPrices, targetLen);
    const { values: ethSlice } = sliceByRange(prices.dates, ethPrices, targetLen);

    // Build series using lib/metrics
    // pricesToCumulativePercent returns same length as input, starting at 0%
    const seriesData: Array<{ name: string; values: number[]; color: string; areaStyle: boolean }> = [];

    // Token series
    seriesData.push({
      name: tkr,
      values: pricesToCumulativePercent(priceSlice),
      color: getSeriesColorForName(tkr),
      areaStyle: true,
    });

    // BTC series (if not the token itself)
    if (tkr !== 'BTC') {
      seriesData.push({
        name: 'BTC',
        values: pricesToCumulativePercent(btcSlice),
        color: getSeriesColorForName('BTC'),
        areaStyle: true,
      });
    }

    // ETH series (if not the token itself)
    if (tkr !== 'ETH') {
      seriesData.push({
        name: 'ETH',
        values: pricesToCumulativePercent(ethSlice),
        color: getSeriesColorForName('ETH'),
        areaStyle: true,
      });
    }

    return { dates: dateSlice, series: seriesData };
  }, [prices.values, prices.dates, btcPrices, ethPrices, range, tkr]);

  // Hide if no data
  if (!isLoading && prices.values.length === 0) {
    return null;
  }

  return (
    <ChartCard
      headerIcon={
        <div className="w-6 h-6 rounded-md bg-blue-500/15 text-blue-400 flex items-center justify-center">
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor">
            <rect x="4" y="10" width="3" height="8" rx="1"></rect>
            <rect x="10" y="6" width="3" height="12" rx="1"></rect>
            <rect x="16" y="13" width="3" height="5" rx="1"></rect>
          </svg>
        </div>
      }
      title="Returns vs BTC/ETH"
      subtitle="Cumulative return from start of range"
      isLoading={isLoading}
      loadingVariant="chart"
      actions={
        <TimeRangePicker
          options={['30d', '90d', '180d', '365d', 'All'] as const}
          value={range}
          onChange={setRange}
          theme="blue"
        />
      }
    >
      <TimeSeriesChart
        dates={dates}
        series={series}
        height={320}
        yAxisFormat="percent"
        tooltipFormatter={(params) => {
          const items = params as Array<{ axisValue: string; seriesName: string; value: number; marker: string }>;
          if (!items || items.length === 0) return '';
          const dateStr = items[0]?.axisValue || '';
          const formattedDate = formatDateLabel(dateStr, 'medium');
          let tooltip = `<div style="font-size:11px;font-weight:500;margin-bottom:4px;color:#9ca3af;">${formattedDate}</div>`;
          const sorted = [...items].sort((a, b) => (b.value || 0) - (a.value || 0));
          sorted.forEach((item) => {
            const val = Number(item.value);
            const color = val >= 0 ? '#10b981' : '#ef4444';
            tooltip += `${item.marker} ${item.seriesName}: <span style="color:${color};">${val.toFixed(1)}%</span><br/>`;
          });
          return tooltip;
        }}
      />
    </ChartCard>
  );
};

export default TokenVsBenchmarksReturnsChart;
